import { useEffect, useMemo, useState } from 'react';
import styles from './filters.module.scss';
import ButtonShared from '../../../../../components/Button/Button';
import SelectShared from '../../../../../components/Select';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ClickAwayListener } from '@mui/material';
import { YEARS } from '../../../../../constants/Publication';
import CheckboxShared from '../../../../../components/Checkbox/Checkbox';
import { FiltersMyPublicationsProps } from '../../../../../interfaces/components';
import { useProfileRole, useToast } from '../../../../../helpers/hooks';
import { capitalize } from '../../../../../helpers/app';

const Filters = ({
    user,
    filters,
    getBranches,
    branches,
    cleanBranches,
    getSellers,
    sellers,
    cleanSellers,
    getStatusPublication,
    status,
    cleanStatusPublication,
    setStatesMyPublications,
    setBranchesMyPublications,
    setYearsMyPublications,
    setSellersMyPublications
}: FiltersMyPublicationsProps) => {
    const toast = useToast();
    const profileRole = useProfileRole();
    const [openFilters, setOpenFilters] = useState<boolean>(false);

    const onClickAway = () => {
        if (openFilters) {
            setOpenFilters(false);
        }
    }

    const handleChangeStates = (state: any) => {
        if (state?.isSelected) {
            const statesFiltered = filters?.states.filter((item: any) => item?.id !== state?.id);
            setStatesMyPublications(statesFiltered);
        } else {
            setStatesMyPublications([...filters?.states, state]);
        }
    }

    const handleChangeBranches = (branch: any) => {
        if (branch?.isSelected) {
            const branchesFiltered = filters?.branches.filter((item: any) => item?.id !== branch?.id);
            setBranchesMyPublications(branchesFiltered);
        } else {
            setBranchesMyPublications([...filters?.branches, branch]);
        }
    }

    const handleChangeYears = (year: any) => {
        if (year?.isSelected) {
            const yearsFiltered = filters?.years.filter((item: any) => item?.name !== year?.name);
            setYearsMyPublications(yearsFiltered);
        } else {
            setYearsMyPublications([...filters?.years, year]);
        }
    }

    const handleChangeSellers = (seller: any) => {
        if (seller?.isSelected) {
            const sellersFiltered = filters?.sellers.filter((item: any) => item?.id !== seller?.id);
            setSellersMyPublications(sellersFiltered);
        } else {
            setSellersMyPublications([...filters?.sellers, seller]);
        }
    }

    const statesNormalized: any = useMemo(() => {
        return status?.data && status?.data.map((state: any) => (
            capitalize(state?.name) !== "Eliminada" &&
            {
                id: state?._id,
                name: capitalize(state?.name),
                isNotSelected: <CheckboxShared disabled />,
                isSelected: filters?.states.some((item: any) => item?.id === state?._id),
                check: <CheckboxShared disabled checked />
            }
        ))
    }, [status?.data, filters?.states]);

    const branchesNormalized: any = useMemo(() => {
        return branches?.data && branches?.data.map((branch: any) => (
            {
                id: branch?._id,
                name: capitalize(branch?.name),
                isNotSelected: <CheckboxShared disabled />,
                isSelected: filters?.branches.some((item: any) => item?.id === branch?._id),
                check: <CheckboxShared disabled checked />
            }
        ))
    }, [branches?.data, filters?.branches]);

    const yearsNormalized: any = useMemo(() => {
        return YEARS && YEARS.map((year: any, index: number) => (
            {
                id: index,
                name: year,
                isNotSelected: <CheckboxShared disabled />,
                isSelected: filters?.years.some((item: any) => item?.name === year),
                check: <CheckboxShared disabled checked />
            }
        ))
    }, [YEARS, filters?.years]);

    const sellersNormalized: any = useMemo(() => {
        return sellers?.data && sellers?.data.map((seller: any) => (
            {
                id: seller?._id,
                name: `${capitalize(seller?.name)} ${capitalize(seller?.last_name)}`,
                isNotSelected: <CheckboxShared disabled />,
                isSelected: filters?.sellers.some((item: any) => item?.id === seller?._id),
                check: <CheckboxShared disabled checked />
            }
        ))
    }, [sellers?.data, filters?.sellers]);

    useEffect(() => {
        if (branches?.fetching === "failure") {
            toast("Ha ocurrido un error al mostrar las sucursales");
            cleanBranches();
        }
        if (branches?.fetching === "success") {
            cleanBranches();
        }
    }, [branches?.fetching]);

    useEffect(() => {
        if (sellers?.fetching === "failure") {
            toast("Ha ocurrido un error al mostrar los vendedores");
            cleanSellers();
        }
        if (sellers?.fetching === "success") {
            cleanSellers();
        }
    }, [sellers?.fetching]);

    useEffect(() => {
        if (sellers?.fetching === "failure") {
            toast("Ha ocurrido un error al mostrar los estados");
            cleanStatusPublication();
        }
        if (sellers?.fetching === "success") {
            cleanStatusPublication();
        }
    }, [status?.fetching]);

    useEffect(() => {
        if (profileRole(user?.profile?.code) === "admin") {
   
            if (user?.profile?.code !== "admin-brand") {
                if (user?.profile?.code === "admin-dealership") {
                    getBranches(user?.dealership, "branches");
                }
                getSellers(user?.dealership, "dealership", "sellers");
            }
            getStatusPublication();
        }
    }, []);

    return (
        <ClickAwayListener onClickAway={onClickAway} disableReactTree={true}>
            <div className={styles.filters}>
                <ButtonShared
                    onPress={() => setOpenFilters(!openFilters)}
                    icon={<FilterListIcon className={styles.icon} />}
                    className={styles.filterButton}
                    size="small"
                    id="filterButton"
                />
                {
                    openFilters &&
                    <div className={styles.filtersContainer}>
                        <SelectShared
                            label="Estado"
                            name="state"
                            variant="border"
                            className={styles.select}
                            optionsClassName={`${styles.options} ${statesNormalized.length >= 10 && styles.scroll}`}
                            onPressOption={handleChangeStates}
                            options={statesNormalized}
                            multiple
                        />
                        {
                            user?.profile?.code === "admin-dealership" &&
                            <SelectShared
                                label="Sucursal"
                                name="branches"
                                variant="border"
                                className={styles.select}
                                optionsClassName={`${styles.options} ${branchesNormalized.length >= 10 && styles.scroll}`}
                                onPressOption={handleChangeBranches}
                                options={branchesNormalized}
                                multiple
                            />
                        }
                        <SelectShared
                            label="Año"
                            name="state"
                            variant="border"
                            className={styles.select}
                            optionsClassName={`${styles.options} ${yearsNormalized.length >= 10 && styles.scroll}`}
                            onPressOption={handleChangeYears}
                            options={yearsNormalized}
                            multiple
                        />
                        {
                            profileRole(user?.profile?.code) === "admin" &&
                            <SelectShared
                                label="Vendedor"
                                name="seller"
                                variant="border"
                                className={styles.select}
                                optionsClassName={`${styles.options} ${sellersNormalized.length >= 10 && styles.scroll}`}
                                onPressOption={handleChangeSellers}
                                options={sellersNormalized}
                                multiple
                            />
                        }
                    </div>
                }
            </div>
        </ClickAwayListener>
    )
};
export default Filters;